import { keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { StoreIcon } from 'lucide-react';

import type { WarehouseTenantDTO } from '@invenco/warehousing-interface';

import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

const columnHelper = createColumnHelper<WarehouseTenantDTO>();

const columns = [
  columnHelper.accessor((row) => row.tenant?.name ?? '', {
    id: 'tenant',
    header: 'Tenant',
  }),
  columnHelper.accessor((row) => row.tenant?.code ?? '', {
    id: 'tenantCode',
    header: 'Tenant code',
  }),
  columnHelper.accessor((row) => row.warehouse?.name ?? '', {
    id: 'warehouse',
    header: 'Warehouse',
  }),
];

export function TenantList() {
  const { warehousingGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<WarehouseTenantDTO>('/tenants');

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
  });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'warehouseTenants',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        warehousingGateway.getWarehouseTenants(
          { search: input.search, include: { tenant: true, warehouse: true }, cursor, take },
          { signal },
        ),
      ),
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <StoreIcon />
        </HeaderIcon>
        <HeaderTitle>Tenants</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
